import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu } from "lucide-react";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "@/components/ui/sheet";

const NAV = [
  { label: "Your Water", hash: "#curiosity" },
  { label: "Oklahoma Water", hash: "#oklahoma" },
  { label: "In-Home Test", hash: "#test" },
  { label: "Softener vs Filter", hash: "#softener-vs-filter" },
  { label: "Pricing", hash: "#pricing" },
  { label: "FAQ", hash: "#faq" },
];

export default function Header() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const go = (hash) => (e) => {
    e.preventDefault();
    setOpen(false);
    if (window.location.pathname !== "/") {
      navigate("/" + hash);
    } else {
      document.querySelector(hash)?.scrollIntoView({ behavior: "smooth" });
    }
  };

  const goLink = (path) => (e) => {
    e.preventDefault();
    setOpen(false);
    navigate(path);
  };

  return (
    <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-slate-200/80" data-testid="site-header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between gap-10">
        <div className="flex items-center shrink-0">
          <Link to="/" data-testid="header-logo-link" className="flex items-center">
            <img
              src="/assets/whitestone-logo-full.png"
              alt="Whitestone — Premium Water. Peace of Mind."
              className="h-9 sm:h-10 w-auto"
            />
          </Link>
        </div>

        <nav className="hidden lg:flex items-center justify-center flex-1 space-x-6 xl:space-x-8 text-sm font-medium text-slate-700" data-testid="header-nav">
          {NAV.map((item) => (
            <a
              key={item.hash}
              href={item.hash}
              onClick={go(item.hash)}
              data-testid={`nav-${item.hash.slice(1)}`}
              className="whitespace-nowrap hover:text-sky-700 transition-colors"
            >
              {item.label}
            </a>
          ))}
          <Link to="/locations" data-testid="nav-locations" className="whitespace-nowrap hover:text-sky-700 transition-colors">
            Service Areas
          </Link>
        </nav>

        <div className="flex items-center space-x-4 shrink-0">
          <a href="tel:+18555915274" data-testid="header-phone-link" className="hidden xl:inline-flex items-center text-sm font-bold text-[#0f2b44] hover:text-[#1971c2] transition whitespace-nowrap">
            855-591-5274
          </a>
          <a
            href="#contact"
            onClick={go("#contact")}
            data-testid="header-cta-button"
            className="hidden sm:inline-flex items-center justify-center px-5 py-2.5 rounded-lg text-sm font-semibold text-white bg-[#0f2b44] hover:bg-[#1971c2] transition shadow-sm whitespace-nowrap"
          >
            Get Free Water Analysis
          </a>
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button
                data-testid="mobile-menu-button"
                aria-label="Open navigation menu"
                className="lg:hidden inline-flex items-center justify-center w-10 h-10 rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50 transition"
              >
                <Menu className="w-5 h-5" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[300px] bg-white p-0 flex flex-col" data-testid="mobile-menu-drawer">
              <SheetTitle className="sr-only">Whitestone navigation menu</SheetTitle>
              <div className="p-6 border-b border-slate-100">
                <img src="/assets/whitestone-logo-full.png" alt="Whitestone — Premium Water. Peace of Mind." className="h-8 w-auto" />
              </div>
              <nav className="flex flex-col p-4" data-testid="mobile-menu-nav">
                {NAV.map((item) => (
                  <a
                    key={item.hash}
                    href={item.hash}
                    onClick={go(item.hash)}
                    data-testid={`mobile-nav-${item.hash.slice(1)}`}
                    className="px-3 py-3.5 rounded-lg text-base font-medium text-slate-800 hover:bg-slate-50 hover:text-sky-700 transition-colors"
                  >
                    {item.label}
                  </a>
                ))}
                <a
                  href="/locations"
                  onClick={goLink("/locations")}
                  data-testid="mobile-nav-locations"
                  className="px-3 py-3.5 rounded-lg text-base font-medium text-slate-800 hover:bg-slate-50 hover:text-sky-700 transition-colors"
                >
                  Service Areas
                </a>
              </nav>
              <div className="p-4 border-t border-slate-100 mt-auto">
                <a
                  href="#contact"
                  onClick={go("#contact")}
                  data-testid="mobile-menu-cta"
                  className="flex items-center justify-center w-full py-3.5 rounded-xl text-sm font-bold text-white bg-[#0f2b44] hover:bg-[#1971c2] transition"
                >
                  GET MY FREE WATER ANALYSIS
                </a>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
